import React from 'react';
import { payments } from '@square/web-sdk';
import type { Payments } from '@square/web-payments-sdk-types';
import paymentStore from 'Models/PaymentStore';
import {
	CardFieldsFailureReporter,
	CardFieldsFailureReporterProvider,
	CardFieldsState,
	CardFieldsStateProvider,
	mergeCardFieldsState,
} from './PaymentUnavailable';
import { clearSimulationDecisions, shouldSimulatePaymentFailure } from './paymentFailureSimulation';

/**
 * Square-specific provider chunk. Owns the Square Web Payments SDK initialisation. Mirrors
 * <c>StripeProvider</c> in shape so the rest of the payment UI doesn't have to know which
 * provider is active.
 *
 * Lazy-loaded by <c>PaymentProviderWrapper</c> only when the active payment provider is
 * Square — Stripe-only operators (Phase A onwards) will never download the Square SDK.
 *
 * Unlike Stripe's <c>&lt;Elements&gt;</c> provider context, Square's SDK doesn't have an
 * Elements equivalent — the <c>Payments</c> instance is shared globally via React context
 * so children can call <c>payments.card()</c> directly when they need to render a card input.
 */
export interface SquareProviderProps {
	totalPrice: number;
	/**
	 * What the WRAPPER has to say about the card fields — the payment config and the amount payable.
	 * Merged with this chunk's own SDK state below, worst-wins; see StripeProvider for why it cannot
	 * simply be applied around this component.
	 */
	cardFieldsState?: CardFieldsState | null;
	children: React.ReactNode;
}

/** Module-level cache mirroring StripeProvider's stripePromise pattern. */
let paymentsPromise: Promise<Payments | null> | null = null;

export const initializeSquare = (): Promise<Payments | null> => {
	if (!paymentsPromise) {
		try {
			const { publishableKey: applicationId, accountId: locationId } = paymentStore;
			if (!applicationId || !locationId) {
				console.error('Square applicationId / locationId missing from PaymentStore');
				paymentsPromise = Promise.resolve(null);
			} else {
				paymentsPromise = payments(applicationId, locationId);
			}
		} catch (error) {
			console.error('Error initialising Square Web Payments SDK:', error);
			paymentsPromise = Promise.resolve(null);
		}
	}
	return paymentsPromise;
};

/**
 * Clears the cached SDK promise so the next `initializeSquare()` genuinely retries — the module
 * cache otherwise keeps a failed load for the life of the page.
 */
export const resetSquare = (): void => {
	paymentsPromise = null;
};

/**
 * How long to wait for the Square SDK before calling it blocked. Mirrors StripeProvider: a refused
 * request rejects quickly, a blackholed one never settles, so both a catch and a timeout are needed.
 */
const SDK_LOAD_TIMEOUT_MS = 15_000;

type SdkStatus = 'loading' | 'ready' | 'blocked';

/**
 * React context that exposes the resolved Square <c>Payments</c> instance to descendants.
 * <c>SquareCheckoutForm</c> consumes this via <c>useSquarePayments()</c> below.
 */
export const SquarePaymentsContext = React.createContext<Payments | null>(null);

export function useSquarePayments(): Payments | null {
	return React.useContext(SquarePaymentsContext);
}

function SquareProvider({ cardFieldsState: outerCardFieldsState, children }: SquareProviderProps) {
	const [squarePayments, setSquarePayments] = React.useState<Payments | null>(null);

	// Same shape as StripeProvider, for the same reason: the SDK is downloaded from a third-party
	// host, and a network that blocks it used to leave this resolving to null forever with no error
	// and nothing to retry it.
	const [sdkStatus, setSdkStatus] = React.useState<SdkStatus>('loading');
	const [attempt, setAttempt] = React.useState(0);

	React.useEffect(() => {
		// Dev-only, armed from sessionStorage — see paymentFailureSimulation.
		if (shouldSimulatePaymentFailure('blocked')) {
			setSdkStatus('blocked');
			return undefined;
		}

		let cancelled = false;
		let settled = false;
		const timer = setTimeout(() => {
			if (!settled && !cancelled) {
				setSdkStatus('blocked');
			}
		}, SDK_LOAD_TIMEOUT_MS);

		initializeSquare()
			.then(p => {
				settled = true;
				clearTimeout(timer);
				if (cancelled) {
					return;
				}
				setSquarePayments(p);
				setSdkStatus(p ? 'ready' : 'blocked');
			})
			.catch(() => {
				settled = true;
				clearTimeout(timer);
				if (!cancelled) {
					setSdkStatus('blocked');
				}
			});

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [attempt]);

	// Set by the card form when `card.attach()` fails or never settles. Separate from `sdkStatus`
	// for the same reason as Stripe: the SDK itself arrived, so the network is not the problem and
	// re-downloading it would prove nothing. Usually a bad applicationId / locationId pair.
	const [elementFailed, setElementFailed] = React.useState(false);

	const retry = React.useCallback(() => {
		resetSquare();
		clearSimulationDecisions();
		setElementFailed(false);
		setSquarePayments(null);
		setSdkStatus('loading');
		setAttempt(current => current + 1);
	}, []);

	// Stable identity: this goes into a context the card form reads, and the form's mount effect
	// depends on it — a fresh object each render would re-run the attach on every render.
	const failureReporter = React.useMemo<CardFieldsFailureReporter>(
		() => ({ reportElementFailure: () => setElementFailed(true) }),
		[],
	);

	// Reported through context, NOT by returning something else in place of `children`. `children`
	// is CheckOutForm, which owns the form every payment method submits through — replacing it takes
	// cash, EFTPOS, invoice and pay-later down along with the card fields, on the one screen that
	// tells the customer to choose another method.
	//
	// `elementFailed` is tested first: it can only be set once the SDK reported ready, so if it is
	// set it is the most specific thing we know, and reading `sdkStatus` first would report a stale
	// 'ready' as "fine".
	const ownCardFieldsState: CardFieldsState | null = (() => {
		if (elementFailed) {
			return { kind: 'unavailable', reason: 'element', onRetry: retry };
		}
		if (sdkStatus === 'blocked') {
			return { kind: 'unavailable', reason: 'blocked', onRetry: retry };
		}
		if (sdkStatus === 'loading') {
			return { kind: 'loading' };
		}
		return null;
	})();

	const cardFieldsState = mergeCardFieldsState(outerCardFieldsState, ownCardFieldsState);

	return (
		<SquarePaymentsContext.Provider value={squarePayments}>
			<CardFieldsFailureReporterProvider value={failureReporter}>
				<CardFieldsStateProvider value={cardFieldsState}>{children}</CardFieldsStateProvider>
			</CardFieldsFailureReporterProvider>
		</SquarePaymentsContext.Provider>
	);
}

export default SquareProvider;
